import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ChannelSettingsDto,
  DEFAULT_FAQ_BANNER_IMAGE,
  DEFAULT_FAQ_PAGE_DESCRIPTION,
  DEFAULT_FAQ_PAGE_TITLE,
} from '../models/channel-settings.model';
import { isMeaningfulRichText, resolveSettingImageUrl } from '../../shared/utils/channel-setting-media';

@Injectable({ providedIn: 'root' })
export class ChannelSettingsService {
  private readonly http = inject(HttpClient);
  private readonly imageBaseUrl = environment.imageBaseUrl;

  private readonly settingsState = signal<ChannelSettingsDto | null>(null);
  readonly loaded = signal(false);

  readonly settings = this.settingsState.asReadonly();

  readonly faqPageTitleHtml = computed(() => {
    const title = this.settingsState()?.faqPageTitle;
    return isMeaningfulRichText(title) ? title : DEFAULT_FAQ_PAGE_TITLE;
  });

  readonly faqBannerImageSrc = computed(() => {
    const banner = this.settingsState()?.faqBannerImage;
    return (
      resolveSettingImageUrl(this.imageBaseUrl, banner) ?? DEFAULT_FAQ_BANNER_IMAGE
    );
  });

  readonly faqPageDescriptionHtml = computed(() => {
    const description = this.settingsState()?.faqPageDescription;
    return isMeaningfulRichText(description) ? description : DEFAULT_FAQ_PAGE_DESCRIPTION;
  });

  load(): Observable<ChannelSettingsDto> {
    return this.http
      .get<ChannelSettingsDto>(`${environment.apiUrl}Storefront/GetChannelSettings`)
      .pipe(
        tap((settings) => {
          this.settingsState.set(settings);
          this.loaded.set(true);
        }),
        catchError(() => {
          const empty: ChannelSettingsDto = {
            faqBannerImage: null,
            faqPageTitle: null,
            faqPageDescription: null,
          };
          this.settingsState.set(empty);
          this.loaded.set(true);
          return of(empty);
        }),
      );
  }
}
